<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('container.containers') }}
        </span>
        <h3 class="page-title">
          {{ $t('container.containersList') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/kontenery/nowy')">
          <i class="material-icons">add</i>
          {{ $t('container.addNewContainer') }}
        </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="containers-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          :placeholder="$t('container.enterContainerNumber')"
          v-model="filters.search" />

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">
          {{ $t('ui.resetFilters') }}
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="width: 120px;">{{ $t('status.status') }}</th>
            <th scope="col" class="border-0" style="width: 360px;">{{ $t('container.containerNumber') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.deliveryDate') }}</th>
            <th scole="col" class="border-0"></th>
            <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(container, index) of containers"
            :key="'container-' + index">
            <td class="align-middle">
              <d-badge
                v-if="container.status === 'OPEN'"
                theme="success">
                {{ $t('container.open') }}
              </d-badge>
              <d-badge
                v-if="container.status === 'CLOSED'"
                theme="danger">
                {{ $t('container.close') }}
              </d-badge>
              <d-badge
                v-if="!container.status"
                theme="light">
                {{ $t('ui.lack') }}
              </d-badge>
            </td>
            <td class="align-middle">
              {{ container.number }}
            </td>
            <td class="align-middle">
              {{ container.date }}<br>

              <small
                v-if="container.date_changed"
                class="text-danger">
                {{ $t('container.dliveryDateChanged') }}
              </small>
            </td>
            <td class="align-middle">
              <d-button
                v-if="container.date_changed"
                theme="primary"
                @click.native="informAboutChangedDate(container.id)"
                class="mr-2"
                :disabled="infoInProgress">
                <i class="material-icons">mail</i> {{ $t('container.sendMessageToCustomers') }}
              </d-button>
            </td>
            <td class="align-middle" style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/kontenery/edycja/' + container.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                :title="$t('ui.delete')"
                @click.native="removeContainer(container.id, container.can_delete)">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="containers.length === 0">
            <td
              colspan="5"
              class="text-center">
              {{ $t('container.noContainersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import Pagination from './../components/common/Pagination.vue';
import Vue from 'vue';

export default {
  name: 'containers',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded;
    }
  },
  data() {
    return {
      containers: [],
      loaded: false,
      loadError: false,
      infoInProgress: false,
      removeInProgress: false,
      filters: {
        search: ''
      }
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: '/products/containers/items?page=' + this.currentPage,
        listField: 'containers',
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: ''
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['containers-filters-form'], this.debouncedLoadFilteredData);
    },
    removeContainer (containerID, canDelete) {
      if (!canDelete) {
        Vue.swal({
          title: this.$t('order.exisitngProductsInHplOrder'),
          html: this.$t('container.removeProductText'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/products/containers/delete/',
        id: containerID,
        successTitle: this.$t('container.removeContainerSuccessTitle'),
        successText: this.$t('container.removeContainerSuccessText'),
        confirmTitle: this.$t('container.removeContainerConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('container.removeContainerErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    informAboutChangedDate (containerID) {
      this.infoInProgress = true;

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/products/containers/notification-changed-date/' + containerID,
        successTitle: this.$t('container.notificationSendSuccessTitle'),
        successText: this.$t('container.notificationSendSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('container.notificationSendErrorText'),
        successAction: () => {
          this.infoInProgress = false;
          this.loadFilteredData();
        },
        failAction: () => {
          this.infoInProgress = false;
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
</style>

